/**
 * Hooks for client profile management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  ClientProfile,
  ClientProfileWithCaseCount,
  ClientDetailsResponse,
  CreateClientProfileInput,
  UpdateClientProfileInput,
  ListClientProfilesParams,
  DeleteClientOptions,
} from '../types/case-management'
import {
  listClientProfiles,
  listClientProfilesWithCounts,
  getClientProfile,
  getClientProfileDetails,
  createClientProfile,
  updateClientProfile,
  deleteClientProfile,
} from '../services/cases'

// =============================================================================
// useClientProfiles - List and filter client profiles
// =============================================================================

interface UseClientProfilesResult {
  profiles: ClientProfile[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch client profiles with optional filtering
 */
export function useClientProfiles(params?: ListClientProfilesParams): UseClientProfilesResult {
  const [profiles, setProfiles] = useState<ClientProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const search = params?.search
  const status = params?.status
  const limit = params?.limit
  const offset = params?.offset

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listClientProfiles(params)
      setProfiles(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch client profiles')
      setProfiles([])
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, limit, offset])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { profiles, loading, error, refetch: fetch }
}

// =============================================================================
// useClientProfile - Single client profile
// =============================================================================

interface UseClientProfileResult {
  profile: ClientProfile | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single client profile by ID
 */
export function useClientProfile(id: string | null): UseClientProfileResult {
  const [profile, setProfile] = useState<ClientProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!id) {
      setProfile(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getClientProfile(id)
      setProfile(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch client profile')
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { profile, loading, error, refetch: fetch }
}

// =============================================================================
// useClientProfileMutations - Create, update, delete profiles
// =============================================================================

interface UseClientProfileMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  create: (input: CreateClientProfileInput) => Promise<ClientProfile | null>
  update: (id: string, input: UpdateClientProfileInput) => Promise<ClientProfile | null>
  remove: (id: string, options?: DeleteClientOptions) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for client profile mutations (create, update, delete)
 */
export function useClientProfileMutations(): UseClientProfileMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    async (input: CreateClientProfileInput): Promise<ClientProfile | null> => {
      setCreating(true)
      setError(null)

      try {
        const result = await createClientProfile(input)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create client profile')
        return null
      } finally {
        setCreating(false)
      }
    },
    []
  )

  const update = useCallback(
    async (id: string, input: UpdateClientProfileInput): Promise<ClientProfile | null> => {
      setUpdating(true)
      setError(null)

      try {
        const result = await updateClientProfile(id, input)
        return result
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update client profile')
        return null
      } finally {
        setUpdating(false)
      }
    },
    []
  )

  const remove = useCallback(
    async (id: string, options?: DeleteClientOptions): Promise<boolean> => {
      setDeleting(true)
      setError(null)

      try {
        await deleteClientProfile(id, options)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete client profile')
        return false
      } finally {
        setDeleting(false)
      }
    },
    []
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    creating,
    updating,
    deleting,
    error,
    create,
    update,
    remove,
    clearError,
  }
}

// =============================================================================
// useClientProfilesWithCounts - List profiles with case counts
// =============================================================================

interface UseClientProfilesWithCountsResult {
  profiles: ClientProfileWithCaseCount[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch client profiles with case counts (for list views)
 */
export function useClientProfilesWithCounts(
  params?: ListClientProfilesParams
): UseClientProfilesWithCountsResult {
  const [profiles, setProfiles] = useState<ClientProfileWithCaseCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const countsSearch = params?.search
  const countsStatus = params?.status
  const countsLimit = params?.limit
  const countsOffset = params?.offset

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listClientProfilesWithCounts(params)
      setProfiles(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch client profiles')
      setProfiles([])
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countsSearch, countsStatus, countsLimit, countsOffset])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { profiles, loading, error, refetch: fetch }
}

// =============================================================================
// useClientProfileDetails - Client profile with case summary
// =============================================================================

interface UseClientProfileDetailsResult {
  details: ClientDetailsResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch client profile details with case summary
 */
export function useClientProfileDetails(id: string | null): UseClientProfileDetailsResult {
  const [details, setDetails] = useState<ClientDetailsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!id) {
      setDetails(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getClientProfileDetails(id)
      setDetails(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch client profile details')
      setDetails(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { details, loading, error, refetch: fetch }
}
